import { Box, IconButton, MenuItem, Select, Stack, Tab, Tabs, Tooltip, Typography, useTheme } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import MetadataEditor from "../../Components/MetadataEditor/metadata-editor";
import { useSelector } from "react-redux";
import {
  GetFieldUpdate,
  GetIsRightPanelOpen,
  GetSelectedEpisode,
  GetSelectedTitle,
  GetTitleCultures,
  GetTitleLocalizedMeta,
  GetTitleSelectedCulture,
  RemoveTitleCulture,
  SetSelectedEpisode,
  SetTitleSelectedCulture,
} from "../../Redux/Reducers/redux-slice-bundle-maker";
import FormSection from "../../Components/Forms/FormSection/form-section";
import { IDbBaseMetadataValueInfos } from "../../Core/Api/Types/i-db-base-metadata-value-infos";
import ArtworksTable from "./artworks-table";
import ImageIcon from "@mui/icons-material/Image";
import {
  BundleMetadataValidationStatus,
  checkValidationByCountry,
} from "../../Utils/Metadata/utils-metadata-validation";
import RequiredIndicator from "../../Components/Misc/RequiredIndicator";
import { useDispatch } from "react-redux";
import { BaseMetadataType } from "../../Core/Api/Types/base-metadata-type";
import ClearIcon from "@mui/icons-material/Clear";
import ModalConfirmAction, {
  ModalConfirmActionElementProps,
} from "../../Components/Modals/ModalConfirmAction/modal-confirm-action";
import { ModalContext } from "../../Components/Modals";
import { DbCultureInfos } from "../../Core/Api/Types/db-culture-infos";
import UploadIcon from "@mui/icons-material/Upload";
import ModalBundlemakerUploadArtwork, {
  ModalBundlemakerUploadArtworkProps,
} from "../../Components/Modals/ModalBundlemakerUploadArtwork/modal-bundlemaker-upload-artwork";
import ModalEditMetadataDatabase from "../../Components/Modals/ModalEditMetadata/modal-edit-metadata-database";
import ContactlessOutlinedIcon from "@mui/icons-material/ContactlessOutlined";
import FieldInputTitleBundlemaker from "./field-input-title-bundlemaker";
import { assetService } from "../../Core/Api/services";
import useCustomSnackbar from "../../Utils/Hooks/useCustomSnackbar";
import { DbMetadataFieldInfos } from "../../Core/Api/Types/db-metadata-field-infos";
import { useTranslation } from "react-i18next";

interface EditTitleProps {
  handleMetadataChanges: (
    isValid: boolean,
    metadata: IDbBaseMetadataValueInfos[],
    isAllMandatoryFilled: boolean,
    status: DbMetadataFieldInfos
  ) => void;
  currentTitleMetadataValidation;
}

const EditTitleBundlemaker: React.FC<EditTitleProps> = (props) => {
  const { t } = useTranslation();
  const { handleMetadataChanges, currentTitleMetadataValidation } = props;

  const dispatch = useDispatch();
  const theme = useTheme();
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  //------Select store values------

  const titleCultures: DbCultureInfos[] = useSelector(GetTitleCultures);
  const obj = useSelector(GetSelectedTitle);
  const isRightPanelOpen = useSelector(GetIsRightPanelOpen);
  const selectedEpisode = useSelector(GetSelectedEpisode) ?? null;
  const titleMeta = useSelector(GetTitleLocalizedMeta);
  const titleSelectedCulture = useSelector(GetTitleSelectedCulture);
  const updateField = useSelector(GetFieldUpdate);
  const episodeCount = obj?.metadata?.find((x) => x.fieldName === "serieSeasonEpisodeTotalNumber")?.value;
  const enqueueCustomSnackbar = useCustomSnackbar();
  //------local state------

  const [openTab, setOpenTab] = useState(0);
  const [openSubTab, setOpenSubTab] = useState(0);

  //------Memoized values derived from state------

  const titleLanguagesMap = useMemo(() => {
    const map = {};
    if (!obj) return map;
    for (let i = 0; i < titleCultures.length; i++) {
      const mainLanguage = titleCultures[i]?.languagesMap
        ? titleCultures[i]?.languagesMap?.find((x) => x)
        : titleCultures[i]?.languages && titleCultures[i].languages!.length > 0
        ? titleCultures[i]?.languages![0]
        : null;
      if (mainLanguage && mainLanguage.id) {
        map[mainLanguage.id]
          ? map[mainLanguage.id].cultures.push(titleCultures[i])
          : (map[mainLanguage.id] = { language: mainLanguage, cultures: [titleCultures[i]] });
      }
    }
    return map;
  }, [titleCultures.length, obj?.id]);

  const languageCultures = useMemo(
    () =>
      openTab > 0
        ? titleLanguagesMap[Object.keys(titleLanguagesMap).sort((a, b) => parseInt(a) - parseInt(b))[openTab - 1]]
            ?.cultures || []
        : [],
    [openTab, Object.keys(titleLanguagesMap).length, titleCultures.length, obj?.id]
  );

  useEffect(() => {
    dispatch(SetTitleSelectedCulture(languageCultures[openSubTab]));
  }, [openTab, openSubTab, obj?.id, Object.keys(titleLanguagesMap).length, languageCultures, languageCultures.length]);

  useEffect(() => {
    setOpenSubTab(0);
  }, [openTab]);

  //------handle user actions------

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setOpenTab(newValue);
  };

  const handleSubTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setOpenSubTab(newValue);
  };

  const handleRemoveTitleCulture = (e, key) => {
    e.stopPropagation();
    const cultureToRemoveId = languageCultures[openSubTab].id;
    if (!openModal || !cultureToRemoveId) return;
    openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
      displayComment: false,
      title: t("modal:title.removeCultureFromTitle"),
      onConfirm: () => {
        const assetId = obj?.id;
        assetId &&
          assetService.RemoveCulturesFromMetadata(assetId, [cultureToRemoveId]).then((response) => {
            if (response.success) {
              dispatch(RemoveTitleCulture(cultureToRemoveId));
              enqueueCustomSnackbar(response.message ?? t("alert:success.cultureRemovedFromObject"), "success");
            } else {
              enqueueCustomSnackbar(response.message ?? t("alert:error.removingCulture"), "error");
            }
          });
      },
    });
  };

  const handleOpenArtworkModal = useCallback((e) => {
    e.stopPropagation();
    openModal &&
      openModal<ModalBundlemakerUploadArtworkProps>(ModalBundlemakerUploadArtwork, {
        closeModal: () => {},
        cultureId: null,
        languageId: null,
        broadcasterId: null,
        isTitleContext: true,
      });
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        overflowY: "scroll",
        height: "100%",
        marginTop: "-64px",
      }}
    >
      {obj && (
        <>
          <Stack height="25px" spacing={1} mb={3}>
            <Box
              sx={{
                display: "flex",
                position: "fixed",
                width: isRightPanelOpen ? "calc(100% - 640px)" : "-webkit-fill-available",
                justifyContent: "space-between",
                zIndex: 200,
                backgroundColor: theme.palette.background.default,
                marginRight: isRightPanelOpen ? "-24px" : "0px",
              }}
            >
              {episodeCount && episodeCount > 0 && (
                <Box
                  display={"flex"}
                  flexDirection={"row"}
                  justifyContent={"space-between"}
                  width={"236px"}
                  position={"absolute"}
                  right={"24px"}
                  top={"12px"}
                  bottom={"12px"}
                >
                  <Typography lineHeight={"25px"} variant="body2" color={"text.tier"}>
                    {t("page:body.selectedEpisode")}
                  </Typography>
                  {selectedEpisode === null && (
                    <Typography position={"absolute"} top={"4px"} right={"72px"}>
                      {t("common:global")}
                    </Typography>
                  )}
                  <Select
                    sx={{ height: "100%", width: "125px" }}
                    value={selectedEpisode ?? undefined}
                    onChange={(e) => {
                      dispatch(SetSelectedEpisode(e.target.value ? (e.target.value as number) : null));
                    }}
                  >
                    <MenuItem value={undefined}>{t("common:global")}</MenuItem>
                    {Array.apply(null, Array(episodeCount)).map((x, i) => (
                      <MenuItem value={i + 1} key={i}>
                        {t("common:episode")} {i + 1}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              )}
              <Tabs
                sx={{ borderBottom: 1, borderColor: "divider", marginRight: episodeCount ? "200px" : "0px" }}
                variant="scrollable"
                scrollButtons="auto"
                value={openTab}
                onChange={(e, v) => handleTabChange(e, v)}
              >
                <Tab
                  sx={{
                    width: "125px",
                    paddingTop: "0px",
                    paddingBottom: "0px",
                    textOverflow: "ellipsis",
                  }}
                  key={0}
                  label={
                    <Typography sx={{ textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {t("common:global")}
                    </Typography>
                  }
                  iconPosition="end"
                />

                {Object.keys(titleLanguagesMap)
                  .sort((a, b) => parseInt(a) - parseInt(b))
                  .map((k, index) => (
                    <Tab
                      sx={{
                        width: "125px",
                        paddingTop: "0px",
                        paddingBottom: "0px",
                        textOverflow: "ellipsis",
                      }}
                      key={index + 1}
                      label={
                        titleLanguagesMap[k] &&
                        titleLanguagesMap[k].cultures &&
                        titleLanguagesMap[k].cultures.reduce(
                          (acc, c) =>
                            checkValidationByCountry(c, titleMeta, selectedEpisode).filter(
                              (x) => x.status !== BundleMetadataValidationStatus.Ok && !x.optional
                            ).length > 0
                              ? acc + 1
                              : acc,
                          0
                        ) > 0 ? (
                          <>
                            <Tooltip placement="top" title={titleLanguagesMap[k].language?.label?.toString()}>
                              <Typography sx={{ textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {titleLanguagesMap[k].language?.label?.toString().length > 7
                                  ? titleLanguagesMap[k].language?.label?.toString().substring(0, 7) + " ..."
                                  : titleLanguagesMap[k].language?.label?.toString()}
                              </Typography>
                            </Tooltip>
                            <RequiredIndicator sx={{ left: "12px", top: "22px", position: "absolute", zIndex: 1000 }} />
                          </>
                        ) : (
                          <Tooltip placement="top" title={titleLanguagesMap[k].language?.label?.toString()}>
                            <Typography sx={{ textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {titleLanguagesMap[k].language?.label?.toString().length > 7
                                ? titleLanguagesMap[k].language?.label?.toString().substring(0, 7) + " ..."
                                : titleLanguagesMap[k].language?.label?.toString()}
                            </Typography>
                          </Tooltip>
                        )
                      }
                      {...a11yProps(index)}
                    />
                  ))}
              </Tabs>
            </Box>
          </Stack>
          <Stack ml={3} mt={2} spacing={3}>
            <div role="tabpanel" hidden={openTab !== 0} id={`simple-tabpanel-0`} aria-labelledby={`simple-tab-0`}>
              <MetadataEditor
                byPassMetadataTagNames={["providerId", "titleType"]}
                objectIds={[obj.id]}
                onMetadataChange={handleMetadataChanges}
                isBM={true}
              />
              <FormSection
                action={
                  <Tooltip title={t("tooltip:title.uploadArtworks")}>
                    <IconButton onClick={(e) => handleOpenArtworkModal(e)}>
                      <UploadIcon sx={{ width: "24px", height: "24px" }} />
                    </IconButton>
                  </Tooltip>
                }
                sx={{ marginTop: "24px" }}
                icon={<ImageIcon />}
                title={t("form:section.title.artworks")}
                status="todo"
                size="small"
                defaultExpanded={false}
              >
                <ArtworksTable showActions={true} select={false} />
              </FormSection>
              <FormSection
                icon={<ContactlessOutlinedIcon />}
                sx={{ marginTop: "24px" }}
                title={t("form:section.title.databaseConnections")}
                status="todo"
                defaultExpanded
              >
                <ModalEditMetadataDatabase title={obj} />
              </FormSection>
              <FormSection sx={{ marginTop: "24px" }} title={t("form:section.title.worldwideMetadata")}>
                <Stack display="flex" flexDirection="row" flexWrap="wrap" justifyContent="space-between">
                  {titleMeta &&
                    titleMeta
                      .map((x) => x) // clone array
                      .sort((a, b) => {
                        if (
                          b.field?.baseType === BaseMetadataType.DbSynopsesMetadataValueInfos ||
                          b.field?.baseType === BaseMetadataType.DbCastsMetadataValueInfos
                        ) {
                          return 1;
                        } else return -1;
                      })
                      .map((m, i) => (
                        <React.Fragment key={i}>
                          {!(m.field?.baseType === BaseMetadataType.DbLocalizedRatingMultiMetadataValueInfos) && (
                            <FieldInputTitleBundlemaker
                              currentTitleMetadataValidation={currentTitleMetadataValidation}
                              index={i}
                              m={m}
                              triggerChange={
                                updateField.field === `title_${i}` ||
                                (updateField.field === "title_cast" &&
                                  m.field?.baseType === BaseMetadataType.DbCastsMetadataValueInfos)
                                  ? updateField.count
                                  : 0
                              }
                              selectedEpisode={selectedEpisode ?? null}
                            />
                          )}
                        </React.Fragment>
                      ))}
                </Stack>
              </FormSection>
            </div>
          </Stack>
          {openTab > 0 && (
            <Box
              sx={{
                display: "flex",
                position: "fixed",
                width: isRightPanelOpen ? "calc(100% - 640px)" : "-webkit-fill-available",
                justifyContent: "space-between",
                zIndex: 300,
                height: "45px",
                backgroundColor: theme.palette.mode === "light" ? "rgb(232 232 232)" : "rgb(42 42 54)",
                marginTop: openTab ? "48px" : "0px",
                marginRight: isRightPanelOpen ? "-24px" : "0px",
                borderTop: `1px solid ${theme.palette.background.alphaHover}`,
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Tabs
                sx={{
                  minHeight: "45px !important",
                  ".MuiTabs-flexContainer": { height: "41px !important" },
                }}
                variant="fullWidth"
                scrollButtons="auto"
                value={openSubTab}
                onChange={(e, v) => handleSubTabChange(e, v)}
              >
                {languageCultures &&
                  languageCultures.map((c, index) => (
                    <Tab
                      sx={{
                        width: "125px",
                        paddingTop: "0px",
                        paddingBottom: "0px",
                        textOverflow: "ellipsis",
                        position: "relative",
                      }}
                      iconPosition="end"
                      icon={<ClearIcon onClick={(e) => handleRemoveTitleCulture(e, index)} />}
                      key={index}
                      label={
                        titleMeta &&
                        titleMeta.length > 0 &&
                        checkValidationByCountry(c, titleMeta, selectedEpisode).filter(
                          (x) => x.status !== BundleMetadataValidationStatus.Ok && !x.optional
                        ).length > 0 ? (
                          <>
                            <Tooltip placement="top" title={c.englishName?.toString()}>
                              <Typography sx={{ textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {c.englishName?.toString().length > 6
                                  ? c.englishName?.toString().substring(0, 6) + " ..."
                                  : c.englishName?.toString()}
                              </Typography>
                            </Tooltip>
                            <RequiredIndicator sx={{ left: "8px", top: "32px", position: "absolute", zIndex: 1000 }} />
                          </>
                        ) : (
                          <Typography sx={{ textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {c.englishName?.toString().length > 12
                              ? c.englishName?.toString().substring(0, 12) + " ..."
                              : c.englishName?.toString()}
                          </Typography>
                        )
                      }
                    />
                  ))}
              </Tabs>
            </Box>
          )}
          {titleSelectedCulture?.id && (
            <Stack ml={3} mt={6.2} display="flex" flexDirection="row" flexWrap="wrap" justifyContent="space-between">
              {titleMeta &&
                titleMeta
                  .map((x) => x)
                  .sort((a, b) => {
                    if (
                      b.field?.baseType === BaseMetadataType.DbSynopsesMetadataValueInfos ||
                      b.field?.baseType === BaseMetadataType.DbCastsMetadataValueInfos
                    ) {
                      return 1;
                    } else return -1;
                  })
                  .map((m, i) => (
                    <>
                      {!(
                        m.field?.baseType === BaseMetadataType.DbLocalizedRatingMultiMetadataValueInfos &&
                        !m.value?.value.find((x) => x.system.cultureId === titleSelectedCulture.id)
                      ) && (
                        <FieldInputTitleBundlemaker
                          currentTitleMetadataValidation={currentTitleMetadataValidation}
                          index={i}
                          m={m}
                          triggerChange={
                            updateField.field === `title_${i}` ||
                            (updateField.field === "title_cast" &&
                              m.field?.baseType === BaseMetadataType.DbCastsMetadataValueInfos)
                              ? updateField.count
                              : 0
                          }
                          selectedEpisode={selectedEpisode ?? null}
                        />
                      )}
                    </>
                  ))}
            </Stack>
          )}
        </>
      )}
    </Box>
  );
};

export default EditTitleBundlemaker;

function a11yProps(index: number) {
  return {
    id: `asset-details-panel-tab-${index}`,
    "aria-controls": `asset-details-panel-tabpanel-${index}`,
  };
}
